<template>
    <!-- One pill button per farm the caller passes in, kept to a single line in the caller's own
         order (never reshuffled by selection - only the styling changes), with whatever doesn't
         fit collapsing behind the "+N" button (remeasureFarmRow, below). -->
    <div ref="farmRowRef" class="relative flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
        <button
            v-for="farm in visibleFarms"
            :key="farm.farm_id"
            type="button"
            class="shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors"
            :class="isSelected(farm.farm_id) ? 'border-primary-700 bg-primary-700 text-white' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-100'"
            @click="toggleFarm(farm.farm_id)"
        >
            {{ farm.name }}
        </button>
        <button
            v-if="hiddenFarms.length"
            ref="overflowButtonRef"
            type="button"
            class="shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors"
            :class="
                hiddenSelectedCount === 0
                    ? 'border-gray-300 bg-white text-gray-500 hover:bg-gray-100'
                    : hiddenSelectedCount === hiddenFarms.length
                        ? 'border-primary-700 bg-primary-700 text-white'
                        : 'border-primary-700 bg-white text-primary-700 hover:bg-primary-50'
            "
            @click="toggleOverflowPopover"
        >
            +{{ hiddenFarms.length }}
        </button>
        <!-- Off-screen measuring row: identical chip markup for every farm plus a representative "+N"
             chip, rendered with visibility:hidden + position:absolute so it never affects layout -
             remeasureFarmRow reads its children's real widths to decide how many chips fit in
             farmRowRef before this frame paints. These are real <button>s (offsetWidth has to match
             the visible chips exactly), so they are kept out of the a11y tree and the tab order three
             ways: visibility:hidden (which alone already does both), aria-hidden, and inert. -->
        <div ref="measureRowRef" class="pointer-events-none absolute left-0 top-0 flex items-center gap-2" style="visibility: hidden" aria-hidden="true" inert>
            <button v-for="farm in props.farms" :key="farm.farm_id" type="button" class="shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium">
                {{ farm.name }}
            </button>
            <button type="button" class="shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium">+{{ props.farms.length }}</button>
        </div>
    </div>

    <!-- Overflow popover: teleported to <body> and positioned from the "+N" button's own rect, since
         farmRowRef's overflow-hidden (needed to hide the extra chips) would otherwise clip it. Stays
         open across toggles (a newly-selected farm sorts to the front of the main row and leaves this
         list live), closes on an outside click. -->
    <Teleport to="body">
        <div
            v-if="overflowOpen"
            ref="overflowPopoverRef"
            class="fixed z-[1030] max-h-64 w-56 overflow-y-auto rounded-lg border border-gray-200 bg-white p-1.5 shadow-lg"
            :style="overflowPopoverStyle"
        >
            <button
                v-for="farm in hiddenFarms"
                :key="farm.farm_id"
                type="button"
                class="flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                @click="toggleFarm(farm.farm_id)"
            >
                <span class="truncate">{{ farm.name }}</span>
                <Check v-if="isSelected(farm.farm_id)" class="h-4 w-4 shrink-0 text-primary-700" aria-hidden="true" />
            </button>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { Check } from 'lucide-vue-next';
import type { Farm } from '@/types';

const props = defineProps<{
    // Candidate farms for the picker, in the caller's own order (already scoped - e.g. global farm
    // selection + current pig type). Sorting selected-first and the overflow cutoff are this
    // component's own concern.
    farms: Farm[];
    modelValue: number[];
}>();

const emit = defineEmits<{
    'update:modelValue': [number[]];
}>();

function isSelected(farmId: number): boolean {
    return props.modelValue.includes(farmId);
}

function toggleFarm(farmId: number): void {
    emit('update:modelValue', isSelected(farmId) ? props.modelValue.filter((id) => id !== farmId) : [...props.modelValue, farmId]);
}

// How many chips fit on one line, measured against the real rendered chip widths (farm names vary a
// lot in length) rather than a fixed count. Starts optimistic (show everything) so there's no flash
// of a "+N" button before the first measurement runs.
const visibleFarmCount = ref(Infinity);

// Both lists keep `props.farms`' own order untouched - selecting/deselecting a farm only changes its
// styling, never its position or which list it's in. A farm only ever moves between the two lists
// when the farm set itself changes (scope/pig-type), never as a side effect of toggling selection.
const visibleFarms = computed(() => props.farms.slice(0, visibleFarmCount.value));
const hiddenFarms = computed(() => props.farms.slice(visibleFarmCount.value));

// Drives the overflow button's three-state styling: none/some/all of the hidden farms selected.
const hiddenSelectedCount = computed(() => hiddenFarms.value.filter((farm) => isSelected(farm.farm_id)).length);

const farmRowRef = ref<HTMLElement | null>(null);
const measureRowRef = ref<HTMLElement | null>(null);
const overflowButtonRef = ref<HTMLElement | null>(null);
const overflowPopoverRef = ref<HTMLElement | null>(null);
const overflowOpen = ref(false);
const overflowPopoverStyle = ref<{ top: string; left: string }>({ top: '0px', left: '0px' });

// The popover is teleported to <body> (see template) so farmRowRef's overflow-hidden doesn't clip it
// - position it from the button's own rect instead of relying on CSS anchoring, right-aligned to the
// button since it can sit anywhere along the row.
function toggleOverflowPopover(): void {
    if (!overflowOpen.value && overflowButtonRef.value) {
        const rect = overflowButtonRef.value.getBoundingClientRect();
        overflowPopoverStyle.value = { top: `${rect.bottom + 4}px`, left: `${rect.right - 224}px` };
    }
    overflowOpen.value = !overflowOpen.value;
}

onClickOutside(
    overflowPopoverRef,
    () => {
        overflowOpen.value = false;
    },
    { ignore: [overflowButtonRef] }
);

// Sums the off-screen measuring row's real chip widths against the visible row's available width,
// reserving room for the "+N" chip unless every farm already fits without it.
function remeasureFarmRow(): void {
    const container = farmRowRef.value;
    const measureEl = measureRowRef.value;
    if (!container || !measureEl || !props.farms.length) return;

    const children = Array.from(measureEl.children) as HTMLElement[];
    const overflowChipWidth = children[children.length - 1].offsetWidth;
    const chipWidths = children.slice(0, -1).map((chip) => chip.offsetWidth);
    const gap = 8; // matches the row's `gap-2` (0.5rem).
    const available = container.clientWidth;

    let used = 0;
    let count = 0;
    for (let i = 0; i < chipWidths.length; i += 1) {
        const isLastChip = i === chipWidths.length - 1;
        const next = used + chipWidths[i] + (i > 0 ? gap : 0);
        const reserveForOverflowButton = isLastChip ? 0 : overflowChipWidth + gap;
        if (next + reserveForOverflowButton > available) break;
        used = next;
        count += 1;
    }
    visibleFarmCount.value = count;
}

let farmRowObserver: ResizeObserver | null = null;

onMounted(() => {
    if (!farmRowRef.value) return;
    farmRowObserver = new ResizeObserver(() => remeasureFarmRow());
    farmRowObserver.observe(farmRowRef.value);
});

onUnmounted(() => {
    farmRowObserver?.disconnect();
    farmRowObserver = null;
});

// Re-measure whenever the farm set itself changes (scope, pig type). Selection no longer affects
// chip order or membership, so toggling a farm never needs a re-measure.
watch(() => props.farms, () => nextTick(remeasureFarmRow));

// Nothing left to hide -> the button (and its popover) disappear on their own via v-if; drop the
// open flag too so it doesn't reopen stale if the row overflows again later.
watch(
    () => hiddenFarms.value.length,
    (count) => {
        if (count === 0) overflowOpen.value = false;
    }
);
</script>
